<template>
  <MarketingLayout>
    <header class="page-head">
      <p class="eyebrow">RETENTION GUIDES</p>
      <h1>Turn more first visits into <em>regulars.</em></h1>
      <p class="lead">Deep dives into the strategy behind customer retention, the thinking that shapes how WinCard is built.</p>
    </header>

    <section class="guide-grid">
      <router-link v-for="guide in guides" :key="guide.slug" :to="`/retention-guides/${guide.slug}`" class="guide-card">
        <span class="guide-tag">{{ guide.tag }}</span>
        <h3>{{ guide.title }}</h3>
        <p>{{ guide.description }}</p>
        <span class="guide-meta">{{ guide.readTime }}</span>
      </router-link>
    </section>

    <section class="stat-strip">
      <div><b>5–7×</b><span>more expensive to win a new customer than keep one</span></div>
      <div><b>+25%</b><span>average revenue lift from a 5% bump in retention</span></div>
      <div><b>65%</b><span>of a typical business's revenue comes from repeat customers</span></div>
    </section>

    <section class="cta-strip">
      <div>
        <h3>Ready to put this into practice?</h3>
        <p>See how WinCard automates the retention playbook end to end.</p>
      </div>
      <router-link class="button" to="/contact">Get in touch <span>→</span></router-link>
    </section>
  </MarketingLayout>
</template>

<script>
import MarketingLayout from '@/components/MarketingLayout.vue'
import { retentionGuides } from '@/data/guides'
import { useSeo } from '@/composables/useSeo'

export default {
  name: 'RetentionGuidesPage',
  components: { MarketingLayout },
  setup() {
    useSeo({
      title: 'Retention guides',
      description: 'Deep dives into customer retention strategy, loyalty program design, win-back campaigns, referrals, and how to calculate the ROI of repeat customers.',
      path: '/retention-guides',
    });
  },
  data() {
    return { guides: retentionGuides }
  },
}
</script>

<style scoped>
.page-head { max-width: 640px; margin: 0 auto 60px; text-align: center; }
.page-head .eyebrow { justify-content: center; }
.page-head h1 { font-size: 52px; letter-spacing: -2.6px; line-height: .98; margin: 0 0 18px; font-weight: 600; }
.page-head h1 em { font-family: var(--serif); font-weight: 400; }
.page-head .lead { margin: 0 auto; }

.guide-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 60px; }
.guide-card { border: 1px solid #e3e8e4; background: #fff; border-radius: 10px; padding: 24px; display: flex; flex-direction: column; min-height: 200px; }
.guide-tag { font: 500 9px var(--mono); letter-spacing: .8px; color: var(--green); }
.guide-card h3 { font-size: 16px; letter-spacing: -.5px; margin: 12px 0 8px; line-height: 1.25; }
.guide-card p { font-size: 13px; color: var(--muted); line-height: 1.55; margin: 0; flex: 1; }
.guide-meta { font-size: 11px; color: #9aa3a0; margin-top: 14px; }

.stat-strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 60px; background: var(--ink); border-radius: 12px; padding: 36px 30px; }
.stat-strip div { text-align: center; padding: 0 12px; }
.stat-strip b { display: block; font: 44px var(--serif); color: var(--lime); margin-bottom: 8px; }
.stat-strip span { font-size: 12px; color: #d4d5e0; line-height: 1.5; }

.cta-strip { background: var(--cream); border: 1px solid #ebefeb; border-radius: 12px; padding: 34px 40px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.cta-strip h3 { margin: 0 0 4px; font-size: 19px; }
.cta-strip p { margin: 0; color: var(--muted); font-size: 13px; }
.cta-strip .button { white-space: nowrap; }

@media (max-width: 900px) { .guide-grid, .stat-strip { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 650px) { .guide-grid, .stat-strip { grid-template-columns: 1fr; } .page-head h1 { font-size: 40px; } .cta-strip { flex-direction: column; align-items: flex-start; } }
</style>
